import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Link,
  CardMedia,
  Box,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Feed,
  Launch,
  List,
  People,
  Shortcut,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  getNewsfeeds,
  getNotifications,
  getMyChapter,
  updateLoginTime,
  completeNotification,
} from '../../endpoints/dashboardTs';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import dayjs from 'dayjs';
import ReleaseModal from '../Releases/ReleaseModal';
import { releaseNotes } from '../Releases/ReleaseNotes';
import { roles } from '../../utils/variables';
import {
  DashboardNewsfeed,
  DashboardNotification,
  MyChapter,
} from '../../types/dashboard';

// Component Props Interfaces
interface MyChapterLinesProps {
  chapterName?: string;
  chapterId?: string;
}

interface InfoCardProps {
  title: string;
  description: string;
  titleRight?: React.ReactNode;
  link?: string;
  alert?: boolean;
}

interface NotificationTaskCardProps {
  task: DashboardNotification;
  onClearClick: (taskId: string) => void;
}

interface NewsCardProps {
  title: string;
  description: string;
  link?: string;
  image?: string | null;
  titleRight?: React.ReactNode;
}

// MyChapterLines Component
const MyChapterLines: React.FC<MyChapterLinesProps> = ({
  chapterName,
  chapterId,
}) => {
  return (
    <Grid container item>
      <Grid item xs={12} sx={{ p: 1 }}>
        <InfoCard
          title="Chapter Info"
          description="View your chapter information like meeting times, summaries and more."
          link={`/local-chapters/${chapterId}?tab=info`}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 1 }}>
        <InfoCard
          title="Members"
          description="View your chapter members."
          link={`/local-chapters/${chapterId}?tab=members`}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 1 }}>
        <InfoCard
          title="Officers"
          description="View your chapter officers."
          link={`/local-chapters/${chapterId}?tab=leadership`}
        />
      </Grid>
    </Grid>
  );
};

// InfoCard Component
const InfoCard: React.FC<InfoCardProps> = ({
  title,
  description,
  titleRight = null,
  link = '/',
  alert = false,
}) => {
  return (
    <Link href={link} underline="none">
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: alert ? 'rgba(210, 4, 45, 0.1)' : 'white',
          p: 1,
          pt: 0,
          '&:last-child': { pb: 0 },
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Grid container>
            <Grid container item display="flex" justifyContent="space-between">
              <Grid item display="flex">
                <Launch fontSize="small" />
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  fontSize={14}
                  paddingLeft={1}
                  paddingRight={1}
                >
                  {title}
                </Typography>
              </Grid>
              {titleRight && (
                <Grid item display="flex">
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    fontSize={14}
                    paddingLeft={1}
                    paddingRight={1}
                  >
                    {titleRight}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Typography variant="caption" fontSize={12} paddingBottom={1}>
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Link>
  );
};

// NotificationTaskCard: task row with optional link and optional clear control (outside link).
const NotificationTaskCard: React.FC<NotificationTaskCardProps> = ({
  task,
  onClearClick,
}) => {
  const linkUrl = task.link_url ?? undefined;
  const dueDateLabel = task.due_date
    ? dayjs(task.due_date).format('MM/DD/YYYY')
    : '';
  const titleAndDescription = (
    <>
      <Grid container item display="flex" justifyContent="space-between">
        <Grid item display="flex">
          <Launch fontSize="small" />
          <Typography
            variant="body1"
            fontWeight="bold"
            fontSize={14}
            paddingLeft={1}
            paddingRight={1}
          >
            {task.title}
          </Typography>
        </Grid>
        {dueDateLabel && (
          <Grid item display="flex">
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize={14}
              paddingLeft={1}
              paddingRight={1}
            >
              {dueDateLabel}
            </Typography>
          </Grid>
        )}
      </Grid>
      <Typography variant="caption" fontSize={12} paddingBottom={1}>
        {task.description}
      </Typography>
    </>
  );

  return (
    <Card
      sx={{
        boxShadow: 3,
        borderRadius: 2,
        backgroundColor: 'rgba(210, 4, 45, 0.1)',
        p: 1,
        pt: 0,
        '&:last-child': { pb: 0 },
      }}
    >
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Grid container alignItems="flex-start" justifyContent="space-between">
          <Grid item xs={task.allow_clear_on_click ? true : undefined}>
            {linkUrl ? (
              <Link href={linkUrl} underline="none" color="inherit">
                {titleAndDescription}
              </Link>
            ) : (
              titleAndDescription
            )}
          </Grid>
          {task.allow_clear_on_click && (
            <Grid item>
              <IconButton
                size="small"
                aria-label="Clear this notice"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClearClick(task.id);
                }}
                sx={{ color: '#9C1555' }}
              >
                <CheckCircle fontSize="small" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

// NewsCard Component
const NewsCard: React.FC<NewsCardProps> = ({
  title,
  description,
  link = '/',
  image,
  titleRight = null,
}) => {
  return (
    <Card
      sx={{
        boxShadow: 3,
        borderRadius: 2,
        p: 1,
        pt: 0,
        '&:last-child': { pb: 0 },
      }}
    >
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Grid container>
          <Grid item xs={12} display="flex">
            <Grid item display="flex" paddingRight={1}>
              {image && (
                <CardMedia
                  component="img"
                  image={image}
                  alt="News Image"
                  sx={{
                    maxWidth: 70,
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Grid
                container
                item
                display="flex"
                justifyContent="space-between"
              >
                <Grid item display="flex">
                  <Launch fontSize="small" />
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    fontSize={14}
                    paddingLeft={1}
                    paddingRight={1}
                  >
                    {title}
                  </Typography>
                </Grid>
                {titleRight && (
                  <Grid item display="flex">
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      fontSize={14}
                      paddingLeft={1}
                      paddingRight={1}
                    >
                      {titleRight}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" fontSize={12} paddingBottom={1}>
                  {description}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
const Dashboard: React.FC = (): React.JSX.Element => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [clearConfirmTaskId, setClearConfirmTaskId] = useState<string | null>(
    null
  );
  const [clearConfirmOpen, setClearConfirmOpen] = useState<boolean>(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // API calls using TypeScript HTTP client
  const { data: newsfeeds, isFetching: newsfeedsLoading } = useApiGet<
    DashboardNewsfeed[]
  >(['dashboard-newsfeed'], getNewsfeeds, {
    enabled: true,
    retry: 1,
  });

  const { data: tasks, isFetching: tasksLoading } = useApiGet<
    DashboardNotification[]
  >(['dashboard-tasks'], getNotifications, {
    enabled: true,
    retry: 1,
  });

  const {
    data: myChapter,
    isFetching: myChapterLoading,
    refetch,
  } = useApiGet<MyChapter>(['dashboard-my-chapter'], getMyChapter, {
    enabled: true,
    retry: 1,
  });

  const { mutate: loginTime } = useApiSend(
    updateLoginTime,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  // Check release notes and show modal if needed
  useEffect(() => {
    if (releaseNotes.length > 0 && myChapter) {
      const lastReleaseNote = dayjs(releaseNotes[0].releaseDate);
      if (
        !myChapter.last_login ||
        dayjs(myChapter.last_login).isBefore(lastReleaseNote)
      ) {
        setModalOpen(true);
      }
    }
  }, [myChapter]);

  const isLCOfficer = (): boolean => {
    const role = localStorage.getItem('role');
    return role === roles.LCOfficer;
  };

  // P.E.O. Travel shortcut releases March 13 at midnight (user's local time)
  const showPeoTravelShortcut = !dayjs().isBefore(dayjs('2026-03-13'), 'day');

  const handleModalClose = (): void => {
    loginTime(undefined);
    setModalOpen(false);
  };

  const handleClearNoticeClick = (taskId: string): void => {
    setClearConfirmTaskId(taskId);
    setClearConfirmOpen(true);
    setClearError(null);
  };

  const handleClearConfirmClose = (): void => {
    setClearConfirmOpen(false);
    setClearConfirmTaskId(null);
    setClearError(null);
  };

  const handleClearConfirm = async (): Promise<void> => {
    if (!clearConfirmTaskId) return;
    setClearError(null);
    try {
      await completeNotification(clearConfirmTaskId);
      queryClient.invalidateQueries({ queryKey: ['dashboard-tasks'] });
      handleClearConfirmClose();
    } catch (err) {
      setClearError(
        err instanceof Error ? err.message : 'Failed to clear notification'
      );
    }
  };

  // Loading skeleton component
  const LoadingSkeleton = (): React.ReactElement => (
    <Box sx={{ p: 2 }}>
      <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" height={40} />
    </Box>
  );

  return (
    <>
      <Grid container spacing={2}>
        {/* To-Do Items/Notices Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              height: '40vh',
              minHeight: 300,
              overflow: 'auto',
            }}
          >
            <CardHeader
              title="My To-Do Items/Notices"
              subheader="Upcoming To-Do Items or Notices"
              avatar={<List sx={{ color: '#9C1555' }} fontSize="large" />}
            />
            <CardContent sx={{ paddingTop: 0 }}>
              {tasksLoading ? (
                <LoadingSkeleton />
              ) : (
                <Grid container>
                  {tasks?.map((task) => (
                    <Grid item xs={12} sx={{ p: 1 }} key={task.id}>
                      <NotificationTaskCard
                        task={task}
                        onClearClick={handleClearNoticeClick}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* My Chapter Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              height: '40vh',
              minHeight: 300,
              overflow: 'auto',
            }}
          >
            <CardHeader
              title="My Chapter"
              subheader={myChapter?.name}
              avatar={<People sx={{ color: '#9C1555' }} fontSize="large" />}
            />
            <CardContent sx={{ paddingTop: 0 }}>
              {myChapterLoading ? (
                <LoadingSkeleton />
              ) : (
                <MyChapterLines
                  chapterName={myChapter?.name}
                  chapterId={myChapter?.local_chapter_id}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Newsfeed Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              height: '40vh',
              minHeight: 300,
              overflow: 'auto',
            }}
          >
            <CardHeader
              title="Newsfeed"
              subheader="International and S/P/D News"
              avatar={<Feed sx={{ color: '#9C1555' }} fontSize="large" />}
            />
            <CardContent sx={{ paddingTop: 0 }}>
              {newsfeedsLoading ? (
                <LoadingSkeleton />
              ) : (
                <Grid container>
                  {newsfeeds?.map((news) => (
                    <Grid item xs={12} sx={{ p: 1 }} key={news.id}>
                      <Link href={`/newsfeed/view/${news.id}`} underline="none">
                        <NewsCard
                          title={news.title}
                          description={news.description}
                          link={news.link_url ?? undefined}
                          image={news.image_url ?? undefined}
                        />
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* My Shortcuts Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              height: '40vh',
              minHeight: 300,
              overflow: 'auto',
            }}
          >
            <CardHeader
              title="My Shortcuts"
              subheader="Links to Frequently Used Pages"
              avatar={<Shortcut sx={{ color: '#9C1555' }} fontSize="large" />}
            />
            <CardContent sx={{ paddingTop: 0 }}>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <Link href="/feedback/create">
                    <Typography
                      variant="body1"
                      paddingLeft={1}
                      paddingBottom={1}
                    >
                      Contact Us
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Link href="/release-notes">
                    <Typography
                      variant="body1"
                      paddingLeft={1}
                      paddingBottom={1}
                    >
                      Release Notes
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Link href="https://www.peointernational.org" target="_blank">
                    <Typography
                      variant="body1"
                      paddingLeft={1}
                      paddingBottom={1}
                    >
                      Visit International Website
                    </Typography>
                  </Link>
                </Grid>
                {showPeoTravelShortcut && (
                  <Grid item xs={12} md={6}>
                    <Link
                      href="https://peo.ahitravel.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography
                        variant="body1"
                        paddingLeft={1}
                        paddingBottom={1}
                      >
                        P.E.O. Travel
                      </Typography>
                    </Link>
                  </Grid>
                )}
                {isLCOfficer() && (
                  <Grid item xs={12} md={6}>
                    <Link href="/peo-reports">
                      <Typography
                        variant="body1"
                        paddingLeft={1}
                        paddingBottom={1}
                      >
                        P.E.O. Reports
                      </Typography>
                    </Link>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Link href="/training">
                    <Typography
                      variant="body1"
                      paddingLeft={1}
                      paddingBottom={1}
                    >
                      P.E.O. Hub Training
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Link href="/library?whats_new=true">
                    <Typography
                      variant="body1"
                      paddingLeft={1}
                      paddingBottom={1}
                    >
                      What's New? Resources
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <ReleaseModal
          open={modalOpen}
          onClose={handleModalClose}
          isSaving={isSaving}
          savingError={errorSaving}
        />

        <Dialog
          open={clearConfirmOpen}
          onClose={handleClearConfirmClose}
          aria-labelledby="clear-notice-dialog-title"
          aria-describedby="clear-notice-dialog-description"
        >
          <DialogTitle id="clear-notice-dialog-title">
            Clear this notice?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="clear-notice-dialog-description">
              Mark this notice as cleared. It will no longer appear on your
              dashboard.
            </DialogContentText>
            {clearError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {clearError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClearConfirmClose}>Cancel</Button>
            <Button onClick={handleClearConfirm} color="primary" autoFocus>
              Clear
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
};

export default Dashboard;
